import React, { useEffect, useState } from 'react';
import { Paper, Box, TextField, Grid, Typography, Button, Card, CardContent, IconButton, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const ECOMMERCE2_DEFAULT_REVIEWS = {
  title: 'Lo que dicen nuestros fans',
  subtitle: 'Reseñas',
  items: [
    {
      name: 'Martín G.',
      text: 'La Desbordada es una locura. Doble carne con esa salsa barbacoa y la cebolla crispy... no hay vuelta atrás. Mejor smash de Montevideo.',
      stars: 5,
      avatar: 'MG'
    },
    {
      name: 'Valentina S.',
      text: "Pedimos Triple Vicio y Papas smash cane's. Todo espectacular, las salsas caseras hacen la diferencia. Volvemos seguro.",
      stars: 5,
      avatar: 'VS'
    },
    {
      name: 'Federico L.',
      text: 'La Compulsiva con esa mayonesa ahumada es adictiva. Pedidos por WhatsApp super rápido. 10/10.',
      stars: 5,
      avatar: 'FL'
    }
  ]
};

const ReviewsTab = () => {
  const { t } = useTranslation();
  const { config, handleChange, handleAddArrayItem, handleArrayChange, handleRemoveArrayItem, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const templateId = config?.siteConfig?.metadata?.templateId || config?.metadata?.templateId;
  const isEcommerce2 = templateId === 'ecommerce2';
  const reviews = config?.siteConfig?.reviews || { title: '', subtitle: '', items: [] };
  const fallbackReviews = isEcommerce2 ? ECOMMERCE2_DEFAULT_REVIEWS : { title: '', subtitle: '', items: [] };
  const reviewsWithFallback = {
    title: reviews.title || fallbackReviews.title,
    subtitle: reviews.subtitle || fallbackReviews.subtitle,
    items: Array.isArray(reviews.items) && reviews.items.length > 0 ? reviews.items : fallbackReviews.items
  };
  const reviewItems = Array.isArray(reviews.items) ? reviews.items : [];
  const reviewsTitleLabel = t('reviews_title') === 'reviews_title' ? 'Título de la sección' : t('reviews_title');
  const reviewsSubtitleLabel = t('reviews_subtitle') === 'reviews_subtitle' ? 'Subtítulo de la sección' : t('reviews_subtitle');
  const addReviewLabel = t('add_review') === 'add_review' ? 'Agregar Reseña' : t('add_review');

  const reviewsPath = 'siteConfig.reviews';
  const itemsPath = 'siteConfig.reviews.items';
  const isTitleConfigurable = isFieldConfigurable(`${reviewsPath}.title`);
  const isSubtitleConfigurable = isFieldConfigurable(`${reviewsPath}.subtitle`);
  const isItemsConfigurable = isFieldConfigurable(itemsPath);
  const isItemsFixed = isFieldFixed(itemsPath);

  const hasEditableFields = isTitleConfigurable || isSubtitleConfigurable || (isItemsConfigurable && !isItemsFixed);

  useEffect(() => {
    if (!isEcommerce2) return;

    const hasItems = Array.isArray(reviews.items) && reviews.items.length > 0;
    if (!hasItems && isItemsConfigurable && !isItemsFixed) {
      handleChange(itemsPath, ECOMMERCE2_DEFAULT_REVIEWS.items);
    }
    if (!reviews.title && isTitleConfigurable) {
      handleChange(`${reviewsPath}.title`, ECOMMERCE2_DEFAULT_REVIEWS.title);
    }
    if (!reviews.subtitle && isSubtitleConfigurable) {
      handleChange(`${reviewsPath}.subtitle`, ECOMMERCE2_DEFAULT_REVIEWS.subtitle);
    }
  }, [
    handleChange,
    isEcommerce2,
    isItemsConfigurable,
    isItemsFixed,
    isSubtitleConfigurable,
    isTitleConfigurable,
    itemsPath,
    reviews.items,
    reviews.subtitle,
    reviews.title,
    reviewsPath
  ]);

  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      const updates = {};

      if (isTitleConfigurable) updates[`${reviewsPath}.title`] = reviewsWithFallback.title || '';
      if (isSubtitleConfigurable) updates[`${reviewsPath}.subtitle`] = reviewsWithFallback.subtitle || '';
      if (isItemsConfigurable && !isItemsFixed) updates[itemsPath] = reviewsWithFallback.items;

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables');
        return;
      }

      await SitesService.updateFields(prepareFieldUpdates(updates));
      await loadConfig();
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving reviews:', err);
      SnackbarUtils.error(err.response?.data?.message || err.message || (t('error_saving_section') || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        {isTitleConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={reviewsTitleLabel}
              value={reviewsWithFallback.title || ''}
              onChange={(e) => handleChange('siteConfig.reviews.title', e.target.value)}
            />
          </Grid>
        )}
        {isSubtitleConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={reviewsSubtitleLabel}
              value={reviewsWithFallback.subtitle || ''}
              onChange={(e) => handleChange('siteConfig.reviews.subtitle', e.target.value)}
            />
          </Grid>
        )}
        {isItemsConfigurable && !isItemsFixed && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddArrayItem(itemsPath, { name: '', text: '', stars: 5, avatar: '' })}
                variant="outlined"
              >
                {addReviewLabel}
              </Button>
            </Box>
            {reviewsWithFallback.items.map((review, index) => (
              <Card key={`${review.name || 'review'}-${index}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1">{t('review') || 'Reseña'} {index + 1}</Typography>
                    <IconButton onClick={() => handleRemoveArrayItem(itemsPath, index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('name') || 'Nombre'}
                        value={review.name || ''}
                        onChange={(e) => handleArrayChange(itemsPath, index, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('stars') || 'Estrellas'}
                        value={review.stars ?? 5}
                        onChange={(e) => handleArrayChange(itemsPath, index, 'stars', Number(e.target.value) || 0)}
                        inputProps={{ min: 0, max: 5 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t('avatar') || 'Avatar'}
                        value={review.avatar || ''}
                        onChange={(e) => handleArrayChange(itemsPath, index, 'avatar', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('text') || 'Texto'}
                        value={review.text || ''}
                        onChange={(e) => handleArrayChange(itemsPath, index, 'text', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ReviewsTab;
